import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { CreateNewsModal } from './CreateNewsModal'
import Loader from '../../../common/components/Loader'
import { Article, Languages } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { sanitizeDate } from '../../../core/constants/times'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  deleteArticles,
  getArticles,
  publishArticle,
  unpublishArticle,
} from '../../../core/services/articles'
import { getSettingsLanguages } from '../../../core/services/festival-settings'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { Plus, Trash } from 'lucide-react'
import { Icon } from '@/common/components/Icon'
import { ActionsMenu } from '@/components/ActionsMenu'

export const NewsPage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    sortBy,
    setSortBy,
    totalElements,
    setTotalElemenets,
  } = useTableParams()

  const { hasEditPermission } = useCheckPermission({ page: 'NEWS' })

  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [editMode, setEditMode] = useState<boolean>(false)
  const [currentNews, setCurrentNews] = useState<Article>()
  const [news, setNews] = useState<Article[]>([])
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [showCreateNewsModal, setShowCreateNewsModal] = useState<boolean>(false)
  const [loading, setLoading] = useState(true)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const allLanguagesResponse = await getSettingsLanguages()
        if (allLanguagesResponse.status === 200){
          setAllLanguages(allLanguagesResponse.data)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const getData = useCallback(async () => {
    setLoading(true)
    try {
      const newsResponse = await getArticles({
        page,
        size,
        sortBy,
        sortDirection,
      })
      if (newsResponse.status === 200) {
        setNews(newsResponse.data.content)
        setTotalElemenets(newsResponse.data.totalElements)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [page, size, sortBy, editionId, festivalName, sortDirection])

  useEffect(() => {
    getData()
  }, [getData, sortBy, page, size])

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(news.map((n) => n.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  const handlePublish = async () => {
    if (!hasEditPermission) {
      return
    }
    try {
      setLoading(true)
      const { status } = await publishArticle(isCheck)
      if (status === 200) {
        getData()
        setIsCheck([])
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Publish Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleUnpublish = async () => {
    if (!hasEditPermission) {
      return
    }
    try {
      setLoading(true)
      const { status } = await unpublishArticle(isCheck)
      if (status === 200) {
        getData()
        setIsCheck([])
        setIsError(false)
        setShowMessageBox(true)
        setMessage('UnPublish Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handlePublishSingle = async (items: string[]) => {
    if (!hasEditPermission) {
      return
    }
    try {
      setLoading(true)
      const { status } = await publishArticle(items)
      if (status === 200) {
        getData()
        setIsCheck([])
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Publish Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleUnpublishSingle = async (items: string[]) => {
    if (!hasEditPermission) {
      return
    }
    try {
      setLoading(true)
      const { status } = await unpublishArticle(items)
      if (status === 200) {
        getData()
        setIsCheck([])
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Unpublish Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!hasEditPermission) {
      return
    }
    try {
      setLoading(true)
      const { status } = await deleteArticles(isCheck)
      if (status === 204) {
        getData()
        setIsCheck([])
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Delete Successful')
      }
    } catch (err) {
      if (err instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(err.message)
      } else {
        console.error(err)
      }
    } finally {
      setLoading(false)
    }
  }

  const changePage = async (newPage: number) => {
    setPage(newPage)
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
          disabled={!hasEditPermission}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
          disabled={!hasEditPermission}
        />
      ),
      width: '16px',
      sortable: false,
    },
    {
      title: 'Title',
      accessor: 'title',
      sortable: true,

      render: (row) => (
        <div className="max-w-[260px] line-clamp-3">
          <span>{row.title}</span>
        </div>
      ),
    },
    {
      title: 'Image (1010x400)',
      accessor: '',
      render: (row) => (
        <img
          src={row.imageLink}
          className="rounded-2xl w-[180px] h-[70px] object-contain"
        />
      ),
    },
    {
      title: 'Publish Date',
      accessor: 'publishedAt',
      sortable: true,
      render: (row) => <>{sanitizeDate(row.publishedAt)}</>,
    },
    {
      title: 'Published',
      accessor: 'published',
      sortable: true,
      render: (row) => (
        <Checkbox
          onCheckedChange={() => {
            if (row.published) {
              handleUnpublishSingle([row.id])
            } else {
              handlePublishSingle([row.id])
            }
          }}
          checked={row.published}
          disabled={!hasEditPermission}
        />
      ),
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row) => (
        <a
          className="view text-accent-1 underline"
          href="#"
          onClick={async (e) => {
            e.preventDefault()
            if (!hasEditPermission) {
              return
            }
            setShowCreateNewsModal(true)
            setCurrentNews(row)
          }}
        >
          Edit
        </a>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="News"
        actions={
          <>
            {hasEditPermission && isCheck.length >= 1 && (
              <>
                <Button
                  variant="destructive"
                  onClick={() => setDeleteConfirmationModal(true)}
                >
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
                <ActionsMenu
                  actions={[
                    { label: 'Publish', onClick: () => handlePublish() },
                    { label: 'Unpublish', onClick: () => handleUnpublish() },
                  ]}
                />
              </>
            )}
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setEditMode(false)
                  setShowCreateNewsModal(true)
                  setCurrentNews(undefined)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="News" className="mb-5" />
        <Table
          handleSortBy={handleSortBy}
          isCheck={isCheck}
          columns={columns}
          data={news}
          columnWidth="20%"
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={changePage}
      />
      <CreateNewsModal
        editMode={editMode}
        setEditMode={setEditMode}
        showCreateModal={showCreateNewsModal}
        news={currentNews}
        page={page + 1}
        setShowCreateModal={setShowCreateNewsModal}
        onCreate={getData}
        onClose={() => {
          setCurrentNews(undefined)
          setShowCreateNewsModal(false)
        }}
        allLanguages={allLanguages}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'News'}
        deleteFunc={handleDelete}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
